import { motion } from 'framer-motion';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About', href: '#about' },
    { name: 'Collections', href: '#collections' },
    { name: 'Brands', href: '#brands' },
    { name: 'Contact', href: '#enquiry' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer
      data-testid="footer-section"
      className="bg-matte-charcoal border-t border-white/10"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_34394223-49cf-482a-8bc6-be88a43ed0db/artifacts/m4j76uto_image.png"
              alt="Tilerra"
              data-testid="footer-logo"
              className="h-12 w-auto mb-6"
            />
            <p className="text-warm-grey leading-relaxed max-w-sm mb-6">
              Premium tile studio curating the finest surfaces from leading
              quality brands. Transforming spaces with materials that define
              character.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/tilerra.in"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 bg-white/5 flex items-center justify-center hover:bg-burgundy/50 transition-colors duration-300"
              >
                <Instagram className="w-4 h-4 text-stone-white" />
              </a>
              <a
                href="mailto:faisal@tilerra.in"
                data-testid="footer-email-icon"
                className="w-10 h-10 bg-white/5 flex items-center justify-center hover:bg-burgundy/50 transition-colors duration-300"
              >
                <Mail className="w-4 h-4 text-stone-white" />
              </a>
              <a
                href="tel:+919535621900"
                data-testid="footer-phone-icon"
                className="w-10 h-10 bg-white/5 flex items-center justify-center hover:bg-burgundy/50 transition-colors duration-300"
              >
                <Phone className="w-4 h-4 text-stone-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-playfair text-lg text-stone-white mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                    className="text-warm-grey hover:text-burgundy transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-playfair text-lg text-stone-white mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-burgundy mt-1 flex-shrink-0" />
                <span
                  data-testid="footer-address"
                  className="text-warm-grey text-sm leading-relaxed"
                >
                  41, St Johns Church Rd,
                  <br />
                  Bharati Nagar, Bengaluru
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-burgundy flex-shrink-0" />
                <a
                  href="tel:+919535621900"
                  data-testid="footer-phone"
                  className="text-warm-grey text-sm hover:text-burgundy transition-colors"
                >
                  +91 95356 21900
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-burgundy flex-shrink-0" />
                <a
                  href="mailto:faisal@tilerra.in"
                  data-testid="footer-email"
                  className="text-warm-grey text-sm hover:text-burgundy transition-colors"
                >
                  faisal@tilerra.in
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <p
            data-testid="footer-copyright"
            className="text-center text-warm-grey/60 text-xs uppercase tracking-[0.15em]"
          >
            © {currentYear} Tilerra. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
